import type { Properties } from '@mend/types/properties';
import type { User } from '@mend/types/user';
import type { DirectoryProvider } from '#services/api/provider.ts';
import type { ProviderFilter, ProviderFilterSearchValue } from '#utils/provider-filter.ts';

import { allProvidersToken, displayedProvidersToken } from '#utils/provider-filter.ts';

const displayedProvidersSelected = 'dashboard.providers.selected';
const displayedProvidersUnassignedSelected = 'dashboard.providers.unassignedSelected';
const displayedProvidersAllSelected = 'dashboard.providers.allSelected';

export function resolveProviderSeed(options: {
  properties: Properties;
  providers: readonly DirectoryProvider[];
  user: Pick<User, 'id' | 'role'>;
}): ProviderFilter {
  const visibleProviderIds = new Set(options.providers.map((provider) => provider.id));

  if (options.user.role === 'Provider') {
    return visibleProviderIds.has(options.user.id) ? [options.user.id] : [];
  }

  if (options.properties[displayedProvidersAllSelected]) {
    return allProvidersToken;
  }

  const effectiveSelection = new Set<User['id']>();
  const selectedProviderIds = options.properties[displayedProvidersSelected];

  if (Array.isArray(selectedProviderIds)) {
    for (const providerId of selectedProviderIds) {
      if (typeof providerId === 'number' && visibleProviderIds.has(providerId)) {
        effectiveSelection.add(providerId);
      }
    }
  }

  if (options.properties[displayedProvidersUnassignedSelected]) {
    const unassignedProvider = options.providers.find((provider) => provider.isUnassigned);
    if (unassignedProvider) {
      effectiveSelection.add(unassignedProvider.id);
    }
  }

  if (effectiveSelection.size > 0) {
    return [...effectiveSelection];
  }

  return [];
}

export function isDisplayedProvidersFilter(searchParams: URLSearchParams): boolean {
  return searchParams.getAll('pId').includes(displayedProvidersToken);
}

export function resolveProviderFilter(options: {
  searchParams: URLSearchParams;
  providerFilter: ProviderFilter;
  properties: Properties;
  providers: readonly DirectoryProvider[];
  user: Pick<User, 'id' | 'role'>;
}): ProviderFilter {
  if (isDisplayedProvidersFilter(options.searchParams)) {
    const displayedProviders = resolveProviderSeed(options);
    return displayedProviders === allProvidersToken || displayedProviders.length > 0
      ? displayedProviders
      : allProvidersToken;
  }

  return canonicalizeProviderFilter({
    providerFilter: options.providerFilter,
    providers: options.providers,
  });
}

export function canonicalizeProviderFilter(options: {
  providerFilter: ProviderFilter;
  providers: readonly DirectoryProvider[];
}): ProviderFilter {
  if (options.providerFilter === allProvidersToken || options.providers.length === 0) {
    return options.providerFilter;
  }

  const visibleProviderIds = new Set(options.providers.map((provider) => provider.id));
  return [...new Set(options.providerFilter.filter((id) => visibleProviderIds.has(id)))];
}

export function getCanonicalProviderFilterRedirectParams(
  searchParams: URLSearchParams,
  providerFilter: ProviderFilter
): URLSearchParams | null {
  if (isDisplayedProvidersFilter(searchParams)) {
    const currentValues = searchParams.getAll('pId');
    if (currentValues.length === 1 && currentValues[0] === displayedProvidersToken) {
      return null;
    }

    const redirectParams = new URLSearchParams(searchParams);
    setProviderFilterSearchParams(redirectParams, displayedProvidersToken);
    return redirectParams;
  }

  const canonicalValues =
    providerFilter === allProvidersToken || providerFilter.length === 0
      ? [allProvidersToken]
      : providerFilter.map(String);

  const currentValues = searchParams.getAll('pId');
  if (
    currentValues.length === canonicalValues.length &&
    currentValues.every((value, index) => value === canonicalValues[index])
  ) {
    return null;
  }

  const redirectParams = new URLSearchParams(searchParams);
  setProviderFilterSearchParams(redirectParams, providerFilter);
  return redirectParams;
}

export function setProviderFilterSearchParams(
  searchParams: URLSearchParams,
  providerFilter: ProviderFilterSearchValue
): void {
  searchParams.delete('pId');

  if (providerFilter === displayedProvidersToken) {
    searchParams.set('pId', displayedProvidersToken);
    return;
  }

  if (providerFilter === allProvidersToken || providerFilter.length === 0) {
    searchParams.set('pId', allProvidersToken);
    return;
  }

  for (const providerId of providerFilter) {
    searchParams.append('pId', String(providerId));
  }
}
